<app-page-breadcrumb [pageTitle]="pageTitle"></app-page-breadcrumb>

<app-component-card>
  @if (loading) {
    <div class="py-8 text-center text-gray-500 dark:text-gray-400">Cargando...</div>
  } @else {

    <!-- Tabs (solo al editar) -->
    @if (isEditing && linkId) {
      <div class="border-b border-gray-200 dark:border-gray-700 mb-5">
        <nav class="flex gap-1 -mb-px overflow-x-auto">
          @for (tab of tabs; track tab.key) {
            <button type="button" (click)="setTab(tab.key)"
              class="px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
              [class.border-brand-500]="activeTab === tab.key"
              [class.text-brand-600]="activeTab === tab.key"
              [class.dark:text-brand-400]="activeTab === tab.key"
              [class.border-transparent]="activeTab !== tab.key"
              [class.text-gray-500]="activeTab !== tab.key"
              [class.hover:text-gray-700]="activeTab !== tab.key"
              [class.dark:text-gray-400]="activeTab !== tab.key">
              {{ tab.label }}
            </button>
          }
        </nav>
      </div>

      <!-- Sub-recursos -->
      @if (activeTab === 'availability') {
        <app-booking-availability [bookingLinkId]="linkId"></app-booking-availability>
      }
      @if (activeTab === 'form-fields') {
        <app-booking-form-fields [bookingLinkId]="linkId"></app-booking-form-fields>
      }
      @if (activeTab === 'members') {
        <app-booking-members [bookingLinkId]="linkId"></app-booking-members>
      }
      @if (activeTab === 'reminders') {
        <app-booking-reminder-configs [bookingLinkId]="linkId"></app-booking-reminder-configs>
      }
      @if (activeTab === 'nurture') {
        <app-booking-nurture-emails [bookingLinkId]="linkId"></app-booking-nurture-emails>
      }
    }

    <!-- General (también es la única vista al crear) -->
    @if (!isEditing || activeTab === 'general') {
    <form [formGroup]="form" (ngSubmit)="guardar()" class="space-y-5">

      @if (error) {
        <div class="p-3 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20">
          <p class="text-error-600 dark:text-error-400 text-sm">{{ error }}</p>
        </div>
      }

      <!-- ═══════ Sección: Básico ═══════ -->
      <section class="border border-gray-200 dark:border-gray-700 rounded-lg">
        <button type="button" (click)="toggleSection('basic')"
          class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50">
          Información básica
          <svg class="w-4 h-4 transition-transform" [class.rotate-90]="openSection['basic']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
        @if (openSection['basic']) {
        <div class="p-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Slug <span class="text-xs text-gray-400">(URL pública)</span></label>
            <input type="text" formControlName="slug" (ngModelChange)="onSlugInput($event)"
              placeholder="reunion-ventas"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
            @if (slugChecking) {
              <p class="text-xs text-gray-500 mt-1">Verificando...</p>
            } @else if (slugAvailable === true) {
              <p class="text-xs text-success-600 mt-1">Disponible ✓</p>
            } @else if (slugAvailable === false) {
              <p class="text-xs text-error-600 mt-1">Ya está en uso</p>
            }
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Título <span class="text-error-500">*</span>
            </label>
            <input type="text" formControlName="title"
              class="w-full rounded-lg border bg-transparent px-3 py-2 text-sm dark:bg-gray-900 dark:text-white/90"
              [class.border-gray-300]="!hasError('title')"
              [class.dark:border-gray-700]="!hasError('title')"
              [class.border-error-500]="hasError('title')">
            @if (hasError('title')) { <p class="text-xs text-error-600 mt-1">{{ getError('title') }}</p> }
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Organizador <span class="text-error-500">*</span>
            </label>
            <app-entity-select
              [value]="form.value.idUser"
              (valueChange)="form.patchValue({ idUser: $event })"
              (selected)="onOrganizerSelected($any($event))"
              [displayText]="organizerName"
              [searchFn]="userSearch"
              [labelFn]="userLabel"
              [sublabelFn]="userSublabel"
              [allowClear]="false"
              placeholder="Buscar usuario por nombre o correo…"></app-entity-select>
            <p class="text-xs text-gray-400 mt-1">Quién atenderá las reuniones de esta página.</p>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Tipo de reunión</label>
            <select formControlName="idType"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
              <option [ngValue]="null">— Sin tipo —</option>
              @for (t of meetingTypes; track t.id) {
                <option [ngValue]="t.id">{{ t.label || t.name }}</option>
              }
            </select>
          </div>

          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Descripción</label>
            <textarea formControlName="description" rows="3"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"></textarea>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Modo de agendamiento</label>
            <select formControlName="schedulingMode"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
              <option value="one_on_one">1 a 1</option>
              <option value="round_robin">Round robin</option>
              <option value="round_robin_weighted">Round robin ponderado</option>
              <option value="collective">Colectivo</option>
              <option value="group_event">Evento grupal</option>
            </select>
          </div>

          <div class="flex items-center gap-3 pt-6">
            <input id="isActive" type="checkbox" formControlName="isActive"
              class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500">
            <label for="isActive" class="text-sm text-gray-700 dark:text-gray-300">Activo</label>
          </div>
        </div>
        }
      </section>

      <!-- ═══════ Sección: Scheduling ═══════ -->
      <section class="border border-gray-200 dark:border-gray-700 rounded-lg">
        <button type="button" (click)="toggleSection('scheduling')"
          class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50">
          Slots y buffers
          <svg class="w-4 h-4 transition-transform" [class.rotate-90]="openSection['scheduling']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
        @if (openSection['scheduling']) {
        <div class="p-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Buffer antes (min) *</label>
            <input type="number" formControlName="bufferBeforeMinutes" min="0"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
            @if (hasError('bufferBeforeMinutes')) { <p class="text-xs text-error-600 mt-1">{{ getError('bufferBeforeMinutes') }}</p> }
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Buffer después (min) *</label>
            <input type="number" formControlName="bufferAfterMinutes" min="0"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
            @if (hasError('bufferAfterMinutes')) { <p class="text-xs text-error-600 mt-1">{{ getError('bufferAfterMinutes') }}</p> }
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Aviso mínimo (min) *</label>
            <input type="number" formControlName="minNoticeMinutes" min="0"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
            @if (hasError('minNoticeMinutes')) { <p class="text-xs text-error-600 mt-1">{{ getError('minNoticeMinutes') }}</p> }
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Días máx. de antelación</label>
            <input type="number" formControlName="maxAdvanceDays"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Reuniones máx. por día</label>
            <input type="number" formControlName="maxMeetingsPerDay"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Intervalo de slots (min)</label>
            <input type="number" formControlName="slotIntervalMinutes"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Duración por defecto (min)</label>
            <input type="number" formControlName="defaultDurationMinutes"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Duración de las reuniones agendadas desde esta página.</p>
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Zona horaria</label>
            <select formControlName="timezone"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
              <option value="">— Selecciona zona horaria —</option>
              @if (form.get('timezone')?.value && !timezones.includes(form.get('timezone')?.value)) {
                <option [value]="form.get('timezone')?.value">{{ form.get('timezone')?.value }} (valor guardado)</option>
              }
              @for (tz of timezones; track tz) {
                <option [value]="tz">{{ tz }}</option>
              }
            </select>
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Invitados máx. por slot</label>
            <input type="number" formControlName="maxInviteesPerSlot"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
        </div>
        }
      </section>

      <!-- ═══════ Sección: Branding ═══════ -->
      <section class="border border-gray-200 dark:border-gray-700 rounded-lg">
        <button type="button" (click)="toggleSection('branding')"
          class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50">
          Branding e idioma
          <svg class="w-4 h-4 transition-transform" [class.rotate-90]="openSection['branding']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
        @if (openSection['branding']) {
        <div class="p-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">URL del logo</label>
            <input type="url" formControlName="logoUrl"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Color de marca</label>
            <div class="flex items-center gap-2">
              <input type="color" [value]="brandColorHex" (input)="onBrandColorPicked($any($event.target).value)"
                class="h-10 w-12 flex-shrink-0 cursor-pointer rounded-lg border border-gray-300 bg-transparent p-1 dark:border-gray-700"
                title="Elegir color de marca">
              <input type="text" formControlName="brandColor" placeholder="#3b82f6" maxlength="7"
                class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm font-mono dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
            </div>
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Idioma por defecto</label>
            <select formControlName="defaultLanguage"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
              <option value="es">Español</option>
              <option value="en">English</option>
              <option value="pt">Português</option>
            </select>
          </div>
          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Idiomas soportados</label>
            <div class="flex flex-wrap gap-2">
              @for (lang of languageOptions; track lang.code) {
                <label class="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors"
                  [class.border-brand-500]="isLanguageSelected(lang.code)"
                  [class.bg-brand-50]="isLanguageSelected(lang.code)"
                  [class.dark:bg-brand-500\/10]="isLanguageSelected(lang.code)"
                  [class.text-brand-700]="isLanguageSelected(lang.code)"
                  [class.dark:text-brand-300]="isLanguageSelected(lang.code)"
                  [class.border-gray-300]="!isLanguageSelected(lang.code)"
                  [class.dark:border-gray-700]="!isLanguageSelected(lang.code)"
                  [class.text-gray-700]="!isLanguageSelected(lang.code)"
                  [class.dark:text-gray-300]="!isLanguageSelected(lang.code)">
                  <input type="checkbox" [checked]="isLanguageSelected(lang.code)" (change)="toggleLanguage(lang.code)"
                    class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500">
                  {{ lang.label }}
                </label>
              }
            </div>
          </div>
          <div class="flex items-center gap-3">
            <input id="darkMode" type="checkbox" formControlName="darkMode"
              class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500">
            <label for="darkMode" class="text-sm text-gray-700 dark:text-gray-300">Modo oscuro</label>
          </div>
          <div class="flex items-center gap-3">
            <input id="hideBranding" type="checkbox" formControlName="hideBranding"
              class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500">
            <label for="hideBranding" class="text-sm text-gray-700 dark:text-gray-300">Ocultar branding de la plataforma</label>
          </div>
        </div>
        }
      </section>

      <!-- ═══════ Sección: Video ═══════ -->
      <section class="border border-gray-200 dark:border-gray-700 rounded-lg">
        <button type="button" (click)="toggleSection('video')"
          class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50">
          Canal de videollamada
          <svg class="w-4 h-4 transition-transform" [class.rotate-90]="openSection['video']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
        @if (openSection['video']) {
        <div class="p-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
          <p class="text-sm text-gray-600 dark:text-gray-400">Elige el canal con el que los invitados se conectarán a la reunión:</p>

          <!-- Tarjetas de canal -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            @for (opt of videoProviders; track opt.value) {
              <button type="button" (click)="form.patchValue({ videoProvider: opt.value })"
                class="flex items-start gap-3 rounded-lg border p-3 text-left transition-colors"
                [class.border-brand-500]="form.value.videoProvider === opt.value"
                [class.bg-brand-50]="form.value.videoProvider === opt.value"
                [class.dark:bg-brand-900\/20]="form.value.videoProvider === opt.value"
                [class.ring-1]="form.value.videoProvider === opt.value"
                [class.ring-brand-500]="form.value.videoProvider === opt.value"
                [class.border-gray-200]="form.value.videoProvider !== opt.value"
                [class.dark:border-gray-700]="form.value.videoProvider !== opt.value"
                [class.hover:border-brand-300]="form.value.videoProvider !== opt.value">
                <span class="text-xl leading-none mt-0.5">{{ opt.icon }}</span>
                <span class="min-w-0">
                  <span class="block text-sm font-medium text-gray-900 dark:text-white">{{ opt.label }}</span>
                  <span class="block text-xs text-gray-500 dark:text-gray-400">{{ opt.hint }}</span>
                </span>
              </button>
            }
          </div>

          <!-- URL personalizada (solo si el canal es 'custom') -->
          @if (form.value.videoProvider === 'custom') {
            <div>
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                URL personalizada de la videollamada
              </label>
              <input type="url" formControlName="customVideoUrl" placeholder="https://…"
                class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
            </div>
          }

          <div class="md:w-1/2">
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Teléfono (opcional)</label>
            <input type="text" formControlName="phoneNumber" placeholder="+51 999 888 777"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
        </div>
        }
      </section>

      <!-- ═══════ Sección: Presencial ═══════ -->
      <section class="border border-gray-200 dark:border-gray-700 rounded-lg">
        <button type="button" (click)="toggleSection('location')"
          class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50">
          Ubicación presencial
          <svg class="w-4 h-4 transition-transform" [class.rotate-90]="openSection['location']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
        @if (openSection['location']) {
        <div class="p-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Dirección</label>
            <input type="text" formControlName="physicalAddress"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">URL de mapa / dirección</label>
            <input type="url" formControlName="physicalAddressUrl"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Nombre de sala</label>
            <input type="text" formControlName="roomName"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
        </div>
        }
      </section>

      <!-- ═══════ Sección: Pago ═══════ -->
      <section class="border border-gray-200 dark:border-gray-700 rounded-lg">
        <button type="button" (click)="toggleSection('payment')"
          class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50">
          Pago
          <svg class="w-4 h-4 transition-transform" [class.rotate-90]="openSection['payment']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
        @if (openSection['payment']) {
        <div class="p-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="md:col-span-3 flex items-center gap-3">
            <input id="requiresPayment" type="checkbox" formControlName="requiresPayment"
              class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500">
            <label for="requiresPayment" class="text-sm text-gray-700 dark:text-gray-300">Requiere pago</label>
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Monto</label>
            <input type="number" step="0.01" formControlName="paymentAmount"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Moneda</label>
            <select formControlName="paymentCurrency"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
              @if (form.get('paymentCurrency')?.value && !currenciesHasCode(form.get('paymentCurrency')?.value)) {
                <option [value]="form.get('paymentCurrency')?.value">{{ form.get('paymentCurrency')?.value }} (valor guardado)</option>
              }
              @for (c of currencies; track c.id) {
                <option [value]="c.code">{{ c.code }} — {{ c.name }}</option>
              }
            </select>
          </div>
          <!-- Stripe price ID: dato interno, no editable en la UI (INC 53). Se conserva en el modelo y se sigue enviando al backend. -->
        </div>
        }
      </section>

      <!-- ═══════ Sección: Reschedule ═══════ -->
      <section class="border border-gray-200 dark:border-gray-700 rounded-lg">
        <button type="button" (click)="toggleSection('reschedule')"
          class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50">
          Cancelación y reprogramación
          <svg class="w-4 h-4 transition-transform" [class.rotate-90]="openSection['reschedule']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
        @if (openSection['reschedule']) {
        <div class="p-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Aviso mínimo de cancelación (min)</label>
            <input type="number" formControlName="minCancelNoticeMinutes"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Máx. reprogramaciones</label>
            <input type="number" formControlName="maxReschedules"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
        </div>
        }
      </section>

      <!-- ═══════ Sección: Confirmación / Waiting room / Agenda ═══════ -->
      <section class="border border-gray-200 dark:border-gray-700 rounded-lg">
        <button type="button" (click)="toggleSection('confirmation')"
          class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50">
          Confirmación y sala de espera
          <svg class="w-4 h-4 transition-transform" [class.rotate-90]="openSection['confirmation']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
        @if (openSection['confirmation']) {
        <div class="p-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="flex items-center gap-3">
            <input id="confirmationEmailEnabled" type="checkbox" formControlName="confirmationEmailEnabled"
              class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500">
            <label for="confirmationEmailEnabled" class="text-sm text-gray-700 dark:text-gray-300">Enviar correo de confirmación</label>
          </div>
          <div class="flex items-center gap-3">
            <input id="followupEmailEnabled" type="checkbox" formControlName="followupEmailEnabled"
              class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500">
            <label for="followupEmailEnabled" class="text-sm text-gray-700 dark:text-gray-300">Enviar correo de seguimiento</label>
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Correo de seguimiento después de (min)</label>
            <input type="number" formControlName="followupDelayMinutes"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Tiempo de espera por no presentación (min)</label>
            <input type="number" formControlName="noShowWaitMinutes"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
          <div class="md:col-span-2 flex items-center gap-3">
            <input id="waitingRoomEnabled" type="checkbox" formControlName="waitingRoomEnabled"
              class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500">
            <label for="waitingRoomEnabled" class="text-sm text-gray-700 dark:text-gray-300">Habilitar sala de espera</label>
          </div>
          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Mensaje de sala de espera</label>
            <textarea formControlName="waitingRoomMessage" rows="2"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"></textarea>
          </div>
          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Agenda por defecto</label>
            <p class="text-xs text-gray-400 mb-2">Puntos que verá el invitado en la agenda de la reunión.</p>
            <div class="space-y-2">
              @for (item of agendaItems; track $index; let i = $index) {
                <div class="flex items-center gap-2">
                  <input type="text" [(ngModel)]="item.title" [ngModelOptions]="{ standalone: true }"
                    placeholder="Título del punto (ej. Introducción)"
                    class="flex-1 rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
                  <div class="flex items-center gap-1">
                    <input type="number" min="0" [(ngModel)]="item.duration" [ngModelOptions]="{ standalone: true }"
                      placeholder="min"
                      class="w-20 rounded-lg border border-gray-300 bg-transparent px-2 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
                    <span class="text-xs text-gray-400">min</span>
                  </div>
                  <button type="button" (click)="removeAgendaItem(i)" title="Quitar"
                    class="h-8 w-8 flex-shrink-0 inline-flex items-center justify-center rounded-lg border border-error-300 text-error-600 hover:bg-error-50">
                    ×
                  </button>
                </div>
              }
              @if (agendaItems.length === 0) {
                <p class="text-sm text-gray-500 dark:text-gray-400">Sin puntos de agenda.</p>
              }
            </div>
            <button type="button" (click)="addAgendaItem()"
              class="mt-2 text-sm px-3 py-1.5 rounded-lg font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
              + Agregar punto
            </button>
          </div>
        </div>
        }
      </section>

      <!-- ═══════ Sección: GDPR ═══════ -->
      <section class="border border-gray-200 dark:border-gray-700 rounded-lg">
        <button type="button" (click)="toggleSection('gdpr')"
          class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50">
          GDPR / Privacidad
          <svg class="w-4 h-4 transition-transform" [class.rotate-90]="openSection['gdpr']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
        @if (openSection['gdpr']) {
        <div class="p-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 gap-4">
          <div class="flex items-center gap-3">
            <input id="gdprConsentEnabled" type="checkbox" formControlName="gdprConsentEnabled"
              class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500">
            <label for="gdprConsentEnabled" class="text-sm text-gray-700 dark:text-gray-300">Requerir consentimiento GDPR</label>
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Texto de consentimiento</label>
            <textarea formControlName="gdprConsentText" rows="3"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"></textarea>
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">URL de política de privacidad</label>
            <input type="url" formControlName="gdprPrivacyUrl"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
        </div>
        }
      </section>

      <!-- ═══════ Sección: Embed + Pipeline ═══════ -->
      <section class="border border-gray-200 dark:border-gray-700 rounded-lg">
        <button type="button" (click)="toggleSection('pipeline')"
          class="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50">
          Embed y pipeline
          <svg class="w-4 h-4 transition-transform" [class.rotate-90]="openSection['pipeline']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
        @if (openSection['pipeline']) {
        <div class="p-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Tipo de embed</label>
            <select formControlName="embedType"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
              <option value="standalone">Independiente</option>
              <option value="inline">Incrustado</option>
              <option value="popup">Emergente</option>
            </select>
          </div>
          <div class="flex items-center gap-3 pt-6">
            <input id="autoUpdatePipelineStage" type="checkbox" formControlName="autoUpdatePipelineStage"
              class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500">
            <label for="autoUpdatePipelineStage" class="text-sm text-gray-700 dark:text-gray-300">Auto-mover en pipeline al reservar</label>
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Estado de la reunión al reservar</label>
            <!-- INC 70: estados de reunión (Creado, Confirmado, Finalizado…), no etapas del pipeline de negocios. -->
            <select formControlName="idPipelineStageOnBook"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
              <option [ngValue]="null">— Sin cambio de estado —</option>
              @if (form.get('idPipelineStageOnBook')?.value && !meetingStatusesHasId(form.get('idPipelineStageOnBook')?.value)) {
                <option [ngValue]="form.get('idPipelineStageOnBook')?.value"
                        [title]="'Identificador interno: ' + form.get('idPipelineStageOnBook')?.value">Estado guardado que ya no está disponible</option>
              }
              @for (status of meetingStatuses; track status.id) {
                <option [ngValue]="status.id">{{ status.label || status.name }}</option>
              }
            </select>
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Incremento de puntuación del lead</label>
            <input type="number" formControlName="leadScoreIncrement"
              class="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
          </div>
        </div>
        }
      </section>

      @if (!isEditing) {
        <!-- `info` no existe en el tema: con las clases anteriores este aviso se pintaba sin
             fondo, sin borde y con el color de texto heredado, o sea invisible como aviso. -->
        <div class="p-3 bg-blue-light-50 dark:bg-blue-light-500/10 border border-blue-light-200 dark:border-blue-light-500/20 rounded-lg text-sm text-blue-light-700 dark:text-blue-light-300">
          Al guardar podrás configurar disponibilidad, campos de formulario, miembros, recordatorios y nurture emails en las pestañas correspondientes.
        </div>
      }

      <!-- Botones -->
      <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <app-button variant="outline" type="button" (btnClick)="cancelar()">Cancelar</app-button>
        <app-button variant="primary" type="submit" [disabled]="saving || form.invalid">
          {{ saving ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear') }}
        </app-button>
      </div>
    </form>
    }
  }
</app-component-card>
